import React, { useState, useEffect } from 'react';
import '../../styles/animations.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faMoneyBillWave,
  faCalendarAlt,
  faCheckCircle,
  faExclamationCircle,
  faUser,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

const Overview = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const userData = useSelector((state) => state.user_info[0]?.user);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!userData || !userData.properties || !userData.transactions) {
          throw new Error('User data is incomplete or missing');
        }

        const totalAmount = userData.properties.reduce(
          (sum, property) => sum + (property.value || 0),
          0
        );

        const paidAmount = userData.transactions
          .filter((t) => t.status === 'Success')
          .reduce((sum, transaction) => {
            if (!transaction.amount) {
              console.warn(`Transaction ${transaction.transactionId} is missing amount`);
              return sum;
            }
            return sum + transaction.amount;
          }, 0);

        setDashboardData({
          user: userData,
          totalAmount,
          paidAmount,
          remainingAmount: totalAmount - paidAmount,
          nextEmiDate: userData.properties[0]?.startDate || null,
          nextEmiAmount: userData.properties[0]?.emiAmount || 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userData]); // Add userData as dependency to re-run if it changes

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getAmountFontSize = (amount) => {
    const numStr = Math.floor(amount || 0).toString();
    if (numStr.length > 8) return 'text-xl'; // 100,000,000+
    if (numStr.length > 6) return 'text-2xl'; // 1,000,000+
    return 'text-3xl'; // default
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#1DCD9F]/20 border-l-[#1DCD9F]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-8 w-8 rounded-full bg-[#1DCD9F]/10"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-600">No data available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-6 md:py-8 lg:py-10">
      <div className="max-w-full md:max-w-4xl lg:max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
              Welcome back, <span className="text-[#1DCD9F]">{dashboardData.user.customerName}</span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">Here's your financial overview</p>
          </div>
          <div className="h-12 w-12 rounded-full bg-[#1DCD9F]/10 flex items-center justify-center">
            <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-[#1DCD9F]" />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {/* Total Property Value */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 overflow-hidden">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Total Property Value
                  </p>
                  <h3 className={`${getAmountFontSize(dashboardData.totalAmount)} font-bold text-gray-800 mb-2`}>
                    {formatCurrency(dashboardData.totalAmount)}
                  </h3>
                </div>
                <div className="h-10 w-10 rounded-lg bg-[#1DCD9F]/10 flex items-center justify-center">
                  <FontAwesomeIcon icon={faHome} className="h-5 w-5 text-[#1DCD9F]" />
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 flex items-center text-xs text-gray-500">
                <span className="inline-block w-2 h-2 rounded-full bg-[#1DCD9F] mr-2"></span>
                Across {dashboardData.user.properties.length} {dashboardData.user.properties.length === 1 ? 'property' : 'properties'}
              </div>
            </div>
          </div>

          {/* Amount Paid */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 overflow-hidden">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Amount Paid
                  </p>
                  <h3 className={`${getAmountFontSize(dashboardData.paidAmount)} font-bold text-gray-800 mb-2`}>
                    {formatCurrency(dashboardData.paidAmount)}
                  </h3>
                </div>
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5 text-green-500" />
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-green-500 h-1.5 rounded-full"
                    style={{
                      width: `${
                        dashboardData.totalAmount > 0
                          ? (dashboardData.paidAmount / dashboardData.totalAmount) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                {dashboardData.paidAmount === 0 && dashboardData.user.transactions.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Note: No successful transactions recorded yet.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Remaining Amount */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 overflow-hidden">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Remaining Amount
                  </p>
                  <h3 className={`${getAmountFontSize(dashboardData.remainingAmount)} font-bold text-gray-800 mb-2`}>
                    {formatCurrency(dashboardData.remainingAmount)}
                  </h3>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="h-5 w-5 text-blue-500" />
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                {dashboardData.totalAmount > 0
                  ? ((dashboardData.remainingAmount / dashboardData.totalAmount) * 100).toFixed(1)
                  : '0.0'}
                % of total
              </div>
            </div>
          </div>

          {/* Next EMI */}
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 overflow-hidden">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Next EMI Due
                  </p>
                  <h3 className={`${getAmountFontSize(dashboardData.nextEmiAmount)} font-bold text-gray-800 mb-1`}>
                    {formatCurrency(dashboardData.nextEmiAmount)}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {dashboardData.nextEmiDate ? formatDate(dashboardData.nextEmiDate) : 'N/A'}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="h-5 w-5 text-purple-500" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Properties and Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Recent Transactions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faReceipt} className="h-4 w-4 text-[#1DCD9F] mr-2" />
                Recent Transactions
              </h3>
              <span className="text-xs bg-[#1DCD9F]/10 text-[#1DCD9F] px-2 py-1 rounded-full">
                {dashboardData.user.paymentHistory.length} {dashboardData.user.paymentHistory.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            <div className="divide-y divide-gray-100">
              {dashboardData.user.paymentHistory.length > 0 ? (
                dashboardData.user.paymentHistory.map((transaction, index) => (
                  <div
                    key={index}
                    className="px-5 py-4 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className={`h-10 w-10 rounded-lg flex items-center justify-center mr-3 bg-green-100`}
                        >
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className={`h-4 w-4 text-green-500`}
                          />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-800">
                            {transaction.paymentMethod} Payment
                          </h4>
                          <p className="text-xs text-gray-500">
                            {formatDate(transaction.date)} • {transaction.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-800">
                          {transaction.amount ? formatCurrency(transaction.amount) : 'Amount Missing'}
                        </p>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            transaction.status === 'Success'
                              ? 'bg-green-100 text-green-800'
                              : transaction.status === 'Pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-5 py-4 text-gray-500 text-sm">No transactions found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;